import React, { useEffect, useState } from "react";
import * as jose from "jose";
import ServiceLocator from "../../../../domain/service-locator";

interface ChanelUrlTokenValidatorProps {
  children: React.ReactNode;
}

type ValidationStatus = "validating" | "valid" | "invalid";

// バリデーションを除外するパス一覧
const EXCLUDED_PATHS = ["/invalid", "/entry", "/landing-page"];

const signingKey = import.meta.env.VITE_SIGNING_KEY;
const bypassKey = import.meta.env.VITE_URL_VALIDATION_BYPASS_KEY;

export const ChanelUrlTokenValidator: React.FC<
  ChanelUrlTokenValidatorProps
> = ({ children }) => {
  const [validationStatus, setValidationStatus] =
    useState<ValidationStatus>("validating");
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const guidePlayerModule = ServiceLocator.GuidePlayerModule;

  const currentTrack = guidePlayerModule.currentTrack.watch();

  // パス変更を監視
  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
      setValidationStatus("validating"); // パス変更時にバリデーション状態をリセット
    };

    // popstateイベント（ブラウザの戻る/進むボタン）を監視
    window.addEventListener("popstate", handleLocationChange);

    // pushState/replaceStateも監視するため、元の関数をラップ
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      handleLocationChange();
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      handleLocationChange();
    };

    return () => {
      window.removeEventListener("popstate", handleLocationChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  useEffect(() => {
    const validateToken = async () => {
      // 現在のパスがexcludeリストに含まれている場合はバリデーションをスキップ
      if (EXCLUDED_PATHS.includes(currentPath)) {
        setValidationStatus("valid");
        return;
      }

      try {
        // URLパラメータからaccess_tokenを取得
        const urlParams = new URLSearchParams(window.location.search);

        const bk = urlParams.get("d2XpSPFzjsHd");
        const isPreview = urlParams.get("isPreview");

        if (bk === bypassKey || isPreview === "true") {
          setValidationStatus("valid");
          return;
        }

        const token = urlParams.get("access_token");

        if (!token) {
          console.log("No access token found in URL");
          setValidationStatus("invalid");
          return;
        }

        if (!signingKey) {
          console.error("VITE_ACCESS_KEY environment variable is not set");
          setValidationStatus("invalid");
          return;
        }

        // 秘密鍵をエンコード
        const encodedSecret = new TextEncoder().encode(signingKey);

        // JWTトークンを検証
        const { payload } = await jose.jwtVerify(token, encodedSecret, {
          algorithms: ["HS256"],
        });

        // ペイロードの内容を確認
        if (payload.access_granted === true) {
          console.log("Token validation successful");
          setValidationStatus("valid");
        } else {
          console.log("Invalid token payload");
          setValidationStatus("invalid");
        }
      } catch (error) {
        console.error("Token validation failed:", error);
        setValidationStatus("invalid");
      }
    };

    validateToken();
  }, [currentPath, currentTrack]); // currentPathが変更されるたびに再実行

  // バリデーション結果に応じて処理
  useEffect(() => {
    if (validationStatus === "invalid") {
      // 現在のクエリパラメータを保持して/invalidに遷移
      const currentParams = new URLSearchParams(window.location.search);
      // access_tokenは無効なので削除
      currentParams.delete("access_token");

      window.location.href = currentParams.toString()
        ? `/invalid?${currentParams.toString()}`
        : "/invalid";
    }
  }, [validationStatus]);

  return <>{children}</>;
};
